import { Price } from '@jouzen/ecom-components/v3/Price';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { ImageProps } from '@/app/components/Image';
import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';
import type { ListProduct } from '@/queries/utils/mapProductFragmentToListProduct';

import type { BuildHardwareOrderBaseProps } from '../_types/order';
import { getPriceAndDiscount } from '../_utils/getPriceAndDiscount';
import AddOnModal from '../AddOnModal';
import type { OrderSummaryProps } from '../OrderSummary';

interface ProductInfoBarBaseProps extends Omit<
  OrderSummaryProps,
  'currentRoute' | 'image'
> {
  readonly hsaFsaEligible?: boolean;
  readonly productVariant?: MessageKey;
  readonly thumbnailImage?:
    | Pick<ImageProps, 'alt' | 'localizedAlt' | 'src'>
    | undefined;
  readonly title: MessageKey;
}

export type ProductInfoBarProps = ProductInfoBarBaseProps &
  Pick<
    BuildHardwareOrderBaseProps,
    | 'monthlyMembership'
    | 'order'
    | 'threeYearWarranty'
    | 'twoYearWarranty'
    | 'yearlyMembership'
  >;

interface ProductInfoBarPriceProps {
  readonly className?: string;
  readonly product: ListProduct | undefined;
}

const ProductInfoBarPrice = ({
  className,
  product,
}: ProductInfoBarPriceProps) => {
  const { discount, price } = getPriceAndDiscount(product);

  return (
    <>
      {price && (
        <Price
          className={cx(
            'text-base font-bold text-sandstone-500 md:text-xl',
            className,
          )}
          discount={discount}
          discountStyles="text-gray-400"
          price={price}
          priceCompare
        />
      )}
    </>
  );
};

const ProductInfoBar = ({
  isAddingToCart,
  isSelectingSize,
  onAddToCart,
  onRadioChange,
  order,
  product,
  productVariant,
  thumbnailImage,
  title,
  ...rest
}: ProductInfoBarProps) => {
  return (
    <div
      className="-inset-x-3 grid rounded-lg bg-sandstone-200/85 px-4 py-2 shadow-[0px_6px_30px_-4px_rgba(0,0,0,0.1)] backdrop-blur-sm lg:-inset-x-10 lg:px-8"
      data-cy="product-info-bar"
    >
      <div className="col-span-full row-span-full">
        <div className="flex items-center justify-between">
          <div className="flex justify-start">
            {thumbnailImage && (
              <div className="relative mr-2 size-[67px] max-md:hidden">
                <div className="absolute inset-1">
                  <Image
                    {...thumbnailImage}
                    className="object-contain object-center"
                    fill
                    sizes="(min-width: 768px) 67px"
                  />
                </div>
              </div>
            )}
            <div className="flex flex-col flex-wrap justify-center">
              <Typography
                className="w-fit text-base leading-tighter text-wrap md:text-xl"
                Element="h2"
              >
                <RichText messageKey={title} />
              </Typography>
              {productVariant && (
                <Typography
                  className="text-sm font-normal text-gray-400 max-md:sr-only"
                  Element="p"
                >
                  <RichText messageKey={productVariant} />
                </Typography>
              )}
            </div>
          </div>
          <div className="flex items-center gap-x-3 lg:gap-x-6">
            {product && (
              <>
                <div className="flex flex-col items-center">
                  <ProductInfoBarPrice product={product} />
                </div>
                <AddOnModal
                  data-cy="product-info-bar-add-to-cart-button"
                  location="sticky"
                  isAddingToCart={isAddingToCart}
                  isSelectingSize={isSelectingSize}
                  onAddToCart={onAddToCart}
                  onRadioChange={onRadioChange}
                  order={order}
                  {...rest}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductInfoBar;
